/**
 * Analysis graph builder.
 *
 * Implements `05-` §B.2 and `04-` §17.
 *
 * Per D-16, the analysis pipeline reads only `AnalysisConfig` — the subset
 * of SchedulingConfig that excludes scheduling-mode fields (moveProjects,
 * gapBehavior, applyConstraints). This builder accepts a `Snapshot` (which
 * carries the full SchedulingConfig), but only consults
 * `pickAnalysisConfig(snapshot.config)` for its decisions. This enforces
 * the decoupling contract: changing moveProjects / gapBehavior cannot
 * change analysis output.
 *
 * Phase A scope: structural builder only. CPM forward/latest pass (D-13),
 * getSlack(t1,t2) algorithm (D-14), and cycle handling (D-15) are
 * implemented in `core/analysis/` (Phase B).
 *
 * Decisions surfaced here:
 *  - **D-13** — every task gets a node; containment edges marked separately;
 *    pass implementation in Phase B.
 *  - **D-15** — cycle members handled by Phase B's `analyze()` (omitted from
 *    slack maps). The builder includes them in the graph; the pass filters.
 *  - **D-16** — `AnalysisConfig` lock-list enforced at type level
 *    (`pickAnalysisConfig` below).
 *  - **D-12** — `isCompleted` flag on AnalysisNode so the Phase B pass can
 *    apply the per-subsystem `use_progress` rule (analysis includes
 *    completed tasks but gives them zero slack).
 */

import type { Snapshot, NormalizedTaskID, SchedulingConfig, AnalysisConfig } from "../types";
import type { AnalysisGraph, AnalysisNode, AnalysisEdge, ContainmentEdge } from "./types";
import { normalizeLinkForAnalysis } from "./normalize_link";
import { expandStructuralLink } from "./link_expansion";

/**
 * Build an AnalysisGraph from an immutable Snapshot.
 *
 * Pure: depends only on snapshot identity. The exported wrapper in
 * `core/api.ts` is memoized.
 */
export function buildAnalysisGraph_unmemoized(snapshot: Snapshot): AnalysisGraph {
  const analysisCfg = pickAnalysisConfig(snapshot.config);

  // ── Nodes ──────────────────────────────────────────────────────────
  const nodes = new Map<NormalizedTaskID, AnalysisNode>();
  for (const [id, task] of snapshot.tasks) {
    // Analysis includes EVERY task (leaves + summaries + milestones),
    // unlike scheduling which may skip summaries when moveProjects:false.
    // Per D-11, the adapter has already filtered unscheduled tasks from the
    // snapshot; defensive guard kept for robustness.
    if (task.unscheduled) continue;

    const isCompleted = analysisCfg.useProgress && task.progress === 1;
    // Mirrors v1's `isSummaryTask(t) && !isManuallyScheduledSummary(t)` —
    // a summary whose dates aggregate from descendants (not a project-type
    // task with `auto_scheduling: false` carrying its own fixed dates). The
    // snapshot's `autoScheduling` flag follows v1: `t.auto_scheduling !== false`.
    const derivedFromChildren = task.kind === "summary" && task.autoScheduling !== false;

    nodes.set(id, {
      id,
      kind: task.kind,
      calendar: snapshot.calendars.resolveForTask(id),
      duration: task.duration,
      durationUnit: task.durationUnit,
      startDateTs: task.startDate.getTime(),
      endDateTs: task.endDate.getTime(),
      isCompleted,
      derivedFromChildren,
      constraintType: task.constraintType,
      constraintDateTs: task.constraintDate?.getTime()
    });
  }

  // ── Edges ──────────────────────────────────────────────────────────
  //
  // Phase D Day 20+ fix: expand summary-targeted links to leaf descendants,
  // matching v1's `_getImplicitLinks` semantics (links_common.js:198-280).
  // A link `A → SummaryS` is treated by v1 as N implicit links from `A` to
  // each leaf descendant of `S` (so v1's critical-path / slack walk can
  // propagate through the summary). v2 originally treated the summary as a
  // direct node; that produced wrong slack values when the chain only
  // reaches a successor via a summary (e.g., `IS → Development` in the
  // critical-path demo data, where `Development` is the parent of
  // `Develop System` / `Integrate System` / `Test`). Without expansion the
  // chain hits the summary and terminates there.
  //
  // We mirror the expansion on the source side as well — a link from a
  // summary expands to all its leaf descendants — because the same
  // semantics apply for predecessors. Both sides cartesian when both are
  // summaries (rare). Manually-scheduled summaries (v1 concept) are out of
  // scope here; we treat all summary nodes uniformly per D-16 (analysis
  // pipeline reads only the lock-list config — no `move_projects` / etc.).
  //
  // The expansion rule itself lives in `graph/link_expansion.ts` so the
  // candidate-cycle check (`core/api.ts:findCycleIfAdded`) answers with the
  // same semantics without having to build this graph.
  const edges: AnalysisEdge[] = [];
  const seenEdgeKeys = new Set<string>();
  for (const link of snapshot.links) {
    const sourceTask = snapshot.tasks.get(link.source);
    const targetTask = snapshot.tasks.get(link.target);
    if (!sourceTask || !targetTask) continue;
    if (sourceTask.unscheduled || targetTask.unscheduled) continue;
    if (!nodes.has(link.source) || !nodes.has(link.target)) continue;
    // D-12 / v1 parity: under `useProgress: true`, completed tasks (progress === 1)
    // are excluded from the dependency graph. Links into or out of a
    // completed task are dropped so the task's predecessors don't see it as
    // a "tight" successor (which would make them appear critical) and its
    // successors don't see it as a predecessor pulling their earliest start.
    // The completed task remains as a NODE in the graph so the slack map
    // still reports a zero-slack entry per D-12. v1's links_builder.js
    // applies the same rule via `if (target.progress == 1) includeRelation = false`.
    if (analysisCfg.useProgress) {
      if (sourceTask.progress === 1 || targetTask.progress === 1) continue;
    }
    for (const { source: s, target: t } of expandStructuralLink(snapshot, link)) {
      // The shared helper keeps an explicit self-link (cycle detection wants
      // it); the analysis passes must not see a self-edge.
      if (s === t) continue;
      const sTask = snapshot.tasks.get(s);
      const tTask = snapshot.tasks.get(t);
      if (!sTask || !tTask) continue;
      if (!nodes.has(s) || !nodes.has(t)) continue;
      const key = `${s}|${t}|${link.type}|${link.id}`;
      if (seenEdgeKeys.has(key)) continue;
      seenEdgeKeys.add(key);
      const replicaLink = s === link.source && t === link.target
        ? link
        : { ...link, source: s, target: t };
      edges.push(normalizeLinkForAnalysis(replicaLink, sTask, tTask, snapshot.calendars));
    }
  }

  // ── Containment edges ──────────────────────────────────────────────
  // Always emit containment edges in the analysis graph (independent of
  // moveProjects). They're used by D-14 `getSlack(parent, child)` to find
  // the slack between a summary and one of its descendants.
  const containment: ContainmentEdge[] = [];
  for (const id of nodes.keys()) {
    if (!snapshot.hierarchy.isSummary(id)) continue;
    for (const desc of snapshot.hierarchy.descendantsOf(id)) {
      if (nodes.has(desc)) containment.push({ summary: id, descendant: desc });
    }
  }

  // ── Indices ────────────────────────────────────────────────────────
  const predecessors = new Map<NormalizedTaskID, AnalysisEdge[]>();
  const successors = new Map<NormalizedTaskID, AnalysisEdge[]>();
  for (const e of edges) {
    pushTo(predecessors, e.target, e);
    pushTo(successors,   e.source, e);
  }
  const predecessorsRO = new Map<NormalizedTaskID, readonly AnalysisEdge[]>();
  const successorsRO = new Map<NormalizedTaskID, readonly AnalysisEdge[]>();
  for (const [k, v] of predecessors) predecessorsRO.set(k, v);
  for (const [k, v] of successors) successorsRO.set(k, v);

  return {
    nodes,
    edges,
    containment,
    predecessors: predecessorsRO,
    successors: successorsRO
  };
}

/**
 * Per D-16: explicit narrowing function. The only sanctioned way to derive
 * an `AnalysisConfig` from a full `SchedulingConfig`. Phase B's analysis
 * pass accepts `AnalysisConfig` — never `SchedulingConfig` — making "analysis
 * decoupled from scheduling mode" a type-level invariant.
 *
 * Adding new fields to SchedulingConfig does NOT extend the analysis
 * surface: the function explicitly copies the allowed fields only.
 */
export function pickAnalysisConfig(config: SchedulingConfig): AnalysisConfig {
  return {
    useProgress: config.useProgress,
    scheduleFromEnd: config.scheduleFromEnd
  };
}

function pushTo<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  let arr = map.get(key);
  if (!arr) {
    arr = [];
    map.set(key, arr);
  }
  arr.push(value);
}
